import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { Search, Calendar, FileText, Download, User, Moon, Sun, Plus, Trash2, Settings, ListChecks, FilePlus2, Wallet, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import axios from 'axios';
import ConfigureConceptsModal from './components/ConfigureConceptsModal';
import LiquidacionEditor from './components/LiquidacionEditor';
import SavedSettlements from './components/SavedSettlements';
import EmployeeCombobox from './components/EmployeeCombobox';
import { getActiveConcepts, createSettlement } from './Services/payroll.services';

// Sub-componente de campo de información (etiqueta + valor con buen contraste)
const InfoField = ({ label, value }) => (
    <div className="rounded-lg border bg-card px-4 py-3">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            {label}
        </p>
        <p className="mt-1 text-sm font-semibold text-foreground break-words">
            {value || '—'}
        </p>
    </div>
);

// Sub-componente de tarjeta de valor por hora (con barra de color lateral y multiplicador)
const HOUR_COLOR_CLASSES = {
    gray:   { bar: 'bg-slate-500',   text: 'text-slate-700 dark:text-slate-300' },
    orange: { bar: 'bg-orange-500',  text: 'text-orange-700 dark:text-orange-400' },
    purple: { bar: 'bg-purple-500',  text: 'text-purple-700 dark:text-purple-400' },
    indigo: { bar: 'bg-indigo-500',  text: 'text-indigo-700 dark:text-indigo-400' },
    green:  { bar: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400' },
    blue:   { bar: 'bg-blue-500',    text: 'text-blue-700 dark:text-blue-400' },
};

const HourValueCard = ({ label, multiplier, value, color = 'gray' }) => {
    const c = HOUR_COLOR_CLASSES[color] || HOUR_COLOR_CLASSES.gray;
    return (
        <div className="relative overflow-hidden rounded-lg border bg-card">
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${c.bar}`} />
            <div className="p-3 pl-4">
                <div className="flex items-center justify-between gap-1">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                        {label}
                    </p>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${c.text} bg-muted/50`}>
                        ×{multiplier}
                    </span>
                </div>
                <p className={`mt-1 text-base font-bold ${c.text}`}>
                    {new Intl.NumberFormat('es-CO', {
                        style: 'currency',
                        currency: 'COP',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(value || 0)}
                </p>
            </div>
        </div>
    );
};

// Sub-componente de tarjeta de resumen de horas (con icono y total monetario)
const SUMMARY_COLOR_CLASSES = {
    amber:  { bar: 'bg-amber-500',  icon: 'text-amber-600',  iconBg: 'bg-amber-100 dark:bg-amber-950/40' },
    orange: { bar: 'bg-orange-500', icon: 'text-orange-600', iconBg: 'bg-orange-100 dark:bg-orange-950/40' },
    purple: { bar: 'bg-purple-500', icon: 'text-purple-600', iconBg: 'bg-purple-100 dark:bg-purple-950/40' },
    indigo: { bar: 'bg-indigo-500', icon: 'text-indigo-600', iconBg: 'bg-indigo-100 dark:bg-indigo-950/40' },
    green:  { bar: 'bg-emerald-500',icon: 'text-emerald-600',iconBg: 'bg-emerald-100 dark:bg-emerald-950/40' },
    blue:   { bar: 'bg-blue-500',   icon: 'text-blue-600',   iconBg: 'bg-blue-100 dark:bg-blue-950/40' },
};

const SummaryCard = ({ title, icon: Icon, color = 'amber', hours, value }) => {
    const c = SUMMARY_COLOR_CLASSES[color] || SUMMARY_COLOR_CLASSES.amber;
    return (
        <div className="relative overflow-hidden rounded-lg border bg-card">
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${c.bar}`} />
            <div className="p-4 pl-5">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{title}</p>
                        <p className={`mt-1 text-2xl font-bold tracking-tight ${c.icon}`}>
                            {Number(hours || 0).toFixed(2)}h
                        </p>
                    </div>
                    {Icon && (
                        <div className={`shrink-0 flex h-9 w-9 items-center justify-center rounded-lg ${c.iconBg}`}>
                            <Icon className={`h-4 w-4 ${c.icon}`} />
                        </div>
                    )}
                </div>
                <p className="mt-2 text-sm font-medium text-muted-foreground">
                    Total:{' '}
                    <span className="font-semibold text-foreground">
                        {new Intl.NumberFormat('es-CO', {
                            style: 'currency',
                            currency: 'COP',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        }).format(value || 0)}
                    </span>
                </p>
            </div>
        </div>
    );
};

const EmployeePayrollPage = () => {
    const { user } = useAuth();
    const { toast } = useToast();
    
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [loading, setLoading] = useState(false);
    const [payrollData, setPayrollData] = useState(null);
    const [nightShiftStart, setNightShiftStart] = useState('19:00'); // 7pm
    const [nightShiftEnd, setNightShiftEnd] = useState('06:00'); // 6am
    const [horasALiquidar, setHorasALiquidar] = useState(240); // Horas mensuales a liquidar
    const [holidays, setHolidays] = useState([]); // Array de festivos desde BD
    const [showHolidayModal, setShowHolidayModal] = useState(false);
    const [newHoliday, setNewHoliday] = useState({ fecha: '', nombre: '', tipo: 'Nacional' });
    const [multipliers, setMultipliers] = useState({
        horaBase: 1.0,
        horaExtraDiurna: 1.25,
        horaExtraNocturna: 1.75,
        recargoNocturno: 1.35,
        festivoDiurno: 1.80,
        festivoNocturno: 2.15
    });
    const [showMultiplierModal, setShowMultiplierModal] = useState(false);
    const [editingMultiplier, setEditingMultiplier] = useState(null);
    const [savingMultiplier, setSavingMultiplier] = useState(false);

    // Conceptos de nómina (devengados / deducciones)
    const [concepts, setConcepts] = useState([]);
    const [showConceptsModal, setShowConceptsModal] = useState(false);
    // Liquidación editable
    const [showLiquidacion, setShowLiquidacion] = useState(false);
    const [savingSettlement, setSavingSettlement] = useState(false);
    const [settlementsRefreshKey, setSettlementsRefreshKey] = useState(0);

    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

    // Cargar empleados, festivos, multiplicadores y conceptos al montar
    useEffect(() => {
        fetchEmployees();
        fetchHolidays();
        fetchMultipliers();
        fetchConcepts();
    }, []);

    const fetchConcepts = async () => {
        try {
            const res = await getActiveConcepts();
            if (res?.state) setConcepts(res.data || []);
        } catch (error) {
            console.error('Error fetching concepts:', error);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await axios.post(`${API_URL}/employee/filter`, {
                limit: 1000,
                offset: 1,
                query: { estado: 'Activo' }
            });
            const employeesList = Array.isArray(response.data?.data) 
                ? response.data.data 
                : Array.isArray(response.data) 
                ? response.data 
                : [];
            setEmployees(employeesList);
        } catch (error) {
            console.error('Error fetching employees:', error);
            toast({
                title: 'Error',
                description: 'No se pudieron cargar los empleados.',
                variant: 'destructive',
            });
            setEmployees([]);
        }
    };

    const fetchHolidays = async () => {
        try {
            const response = await axios.get(`${API_URL}/holidays/active`);
            if (response.data?.state && response.data?.data) {
                setHolidays(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching holidays:', error);
        }
    };

    const fetchMultipliers = async () => {
        try {
            const response = await axios.get(`${API_URL}/payroll-config/all`);
            if (response.data?.state) {
                // Si no hay datos, inicializar valores por defecto
                if (!response.data.data || response.data.data.length === 0) {
   
                    await axios.post(`${API_URL}/payroll-config/initialize`);
                    // Volver a obtener los datos después de inicializar
                    const retryResponse = await axios.get(`${API_URL}/payroll-config/all`);
                    if (retryResponse.data?.state && retryResponse.data?.data) {
                        const multipliersObj = {};
                        retryResponse.data.data.forEach(config => {
                            multipliersObj[config.tipo] = config.multiplicador;
                        });
                        setMultipliers(prev => ({ ...prev, ...multipliersObj }));
                    }
                } else {
                    const multipliersObj = {};
                    response.data.data.forEach(config => {
                        multipliersObj[config.tipo] = config.multiplicador;
                    });
                    setMultipliers(prev => ({ ...prev, ...multipliersObj }));
                }
            }
        } catch (error) {
            console.error('Error fetching multipliers:', error);
            toast({
                title: 'Error',
                description: 'No se pudieron cargar las configuraciones de recargos.',
                variant: 'destructive',
            });
        }
    };

    const updateMultiplier = async (id, multiplicador) => {
        if (savingMultiplier) return;
        
        try {
            setSavingMultiplier(true);
            const response = await axios.patch(`${API_URL}/payroll-config/update/${id}`, {
                multiplicador: parseFloat(multiplicador)
            });
            
            if (response.data?.state) {
                await fetchMultipliers();
                setEditingMultiplier(null);
                toast({
                    title: 'Éxito',
                    description: 'Multiplicador actualizado correctamente.',
                });
            } else {
                throw new Error(response.data?.message || 'Error al actualizar');
            }
        } catch (error) {
            console.error('Error updating multiplier:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'No se pudo actualizar el multiplicador.',
                variant: 'destructive',
            });
        } finally {
            setSavingMultiplier(false);
        }
    };

    const addHoliday = async () => {
        if (!newHoliday.fecha || !newHoliday.nombre) {
            toast({
                title: 'Error',
                description: 'Fecha y nombre son requeridos.',
                variant: 'destructive',
            });
            return;
        }

        try {
            // Crear fecha a las 12:00 PM para evitar problemas de zona horaria
            const [year, month, day] = newHoliday.fecha.split('-');
            const fechaAjustada = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
            
            const response = await axios.post(`${API_URL}/holidays/create`, {
                ...newHoliday,
                fecha: fechaAjustada.toISOString(),
                activo: true
            });
            
            if (response.data?.state) {
                await fetchHolidays();
                setNewHoliday({ fecha: '', nombre: '', tipo: 'Nacional' });
                toast({
                    title: 'Éxito',
                    description: 'Festivo agregado correctamente.',
                });
            }
        } catch (error) {
            console.error('Error adding holiday:', error);
            toast({
                title: 'Error',
                description: 'No se pudo agregar el festivo.',
                variant: 'destructive',
            });
        }
    };

    const deleteHoliday = async (id) => {
        try {
            const response = await axios.delete(`${API_URL}/holidays/delete/${id}`);
            if (response.data?.state) {
                await fetchHolidays();
                toast({
                    title: 'Éxito',
                    description: 'Festivo eliminado correctamente.',
                });
            }
        } catch (error) {
            console.error('Error deleting holiday:', error);
            toast({
                title: 'Error',
                description: 'No se pudo eliminar el festivo.',
                variant: 'destructive',
            });
        }
    };

    const fetchPayrollData = async () => {
        if (!selectedEmployee) {
            toast({
                title: 'Advertencia',
                description: 'Selecciona un empleado primero.',
                variant: 'destructive',
            });
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/shifts/employee-payroll`, {
                params: {
                    employeeId: selectedEmployee._id,
                    startDate,
                    endDate
                }
            });
            setPayrollData(response.data);
            setShowLiquidacion(false);
        } catch (error) {
            console.error('Error fetching payroll:', error);
            toast({
                title: 'Error',
                description: 'No se pudo cargar la información de liquidación.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEmployeeChange = (employee) => {
        // El EmployeeCombobox entrega el objeto completo; se mantiene
        // compatibilidad si llega sólo el id.
        const emp =
            employee && typeof employee === 'object'
                ? employee
                : employees.find((e) => e._id === employee);
        setSelectedEmployee(emp || null);
        setPayrollData(null);
        setShowLiquidacion(false);
    };

    const handleSaveSettlement = async (payload) => {
        try {
            setSavingSettlement(true);
            const res = await createSettlement(payload);
            if (res?.state) {
                toast({ title: 'Éxito', description: 'Liquidación guardada correctamente.' });
                setShowLiquidacion(false);
                setSettlementsRefreshKey((k) => k + 1);
            } else {
                throw new Error(res?.message || 'Error');
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error?.response?.data?.message || error.message || 'No se pudo guardar la liquidación.',
                variant: 'destructive',
            });
        } finally {
            setSavingSettlement(false);
        }
    };

    const handleSetCurrentMonth = () => {
        const now = new Date();
        setStartDate(format(startOfMonth(now), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
    };

    // Formatear moneda sin decimales y con separador de miles
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Calcular horas nocturnas dentro de un rango
    const calculateNightHours = (startTime, endTime) => {
        if (!startTime || !endTime) return 0;

        const today = new Date();
        const start = parse(startTime, 'HH:mm', today);
        let end = parse(endTime, 'HH:mm', today);
        
        if (end < start) {
            end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        }

        const nightStart = parse(nightShiftStart, 'HH:mm', today);
        let nightEnd = parse(nightShiftEnd, 'HH:mm', today);
        
        if (nightEnd < nightStart) {
            nightEnd = new Date(nightEnd.getTime() + 24 * 60 * 60 * 1000);
        }

        let nightMinutes = 0;
        const workStart = start.getTime();
        const workEnd = end.getTime();
        const nightStartTime = nightStart.getTime();
        const nightEndTime = nightEnd.getTime();

        if (nightStartTime < nightEndTime) {
            const overlapStart = Math.max(workStart, nightStartTime);
            const overlapEnd = Math.min(workEnd, nightEndTime);
            if (overlapStart < overlapEnd) {
                nightMinutes += (overlapEnd - overlapStart) / (1000 * 60);
            }
        } else {
            const midnight = new Date(today);
            midnight.setHours(23, 59, 59, 999);
            const overlapStart1 = Math.max(workStart, nightStartTime);
            const overlapEnd1 = Math.min(workEnd, midnight.getTime());
            if (overlapStart1 < overlapEnd1) {
                nightMinutes += (overlapEnd1 - overlapStart1) / (1000 * 60);
            }

            const nextDayStart = new Date(today);
            nextDayStart.setDate(nextDayStart.getDate() + 1);
            nextDayStart.setHours(0, 0, 0, 0);
            const overlapStart2 = Math.max(workStart, nextDayStart.getTime());
            const overlapEnd2 = Math.min(workEnd, nightEndTime);
            if (overlapStart2 < overlapEnd2) {
                nightMinutes += (overlapEnd2 - overlapStart2) / (1000 * 60);
            }
        }

        return Math.round((nightMinutes / 60) * 100) / 100;
    };

    // Verificar si una fecha es festiva
    const isFestivo = (fecha) => {
        const fechaStr = format(new Date(fecha), 'yyyy-MM-dd');
        return holidays.some(h => format(new Date(h.fecha), 'yyyy-MM-dd') === fechaStr);
    };

    // Calcular todas las horas por tipo según la nueva lógica
    const calculateHoursByType = (record) => {
        const horasAutorizadas = record.totalHorasAutorizadas || 0;
        const horasNocturnas = calculateNightHours(
            record.horaIngresoAutorizada,
            record.horaSalidaAutorizada
        );
        const horasDiurnas = Math.max(0, horasAutorizadas - horasNocturnas);
        const esFestivo = record.esFestivo || isFestivo(record.fecha);

        let horasBase = 0;
        let horasExtrasDiurnas = 0;
        let horasExtrasNocturnas = 0;
        let recargoNocturno = 0;
        let festivoDiurno = 0;
        let festivoNocturno = 0;

        if (esFestivo) {
            // Si es festivo, todas las horas van a festivo diurno/nocturno
            festivoDiurno = horasDiurnas;
            festivoNocturno = horasNocturnas;
        } else {
            // No es festivo: calcular horas base, extras y recargo nocturno
            horasBase = horasAutorizadas;
            recargoNocturno = horasNocturnas;
        }

        return {
            horasBase,
            horasExtrasDiurnas,
            horasExtrasNocturnas,
            recargoNocturno,
            festivoDiurno,
            festivoNocturno,
            totalHoras: horasAutorizadas
        };
    };

    // Calcular totales de todos los registros
    const calculateTotals = () => {
        if (!payrollData) return null;

        let totalHorasBase = 0;
        let totalHorasExtrasDiurnas = 0;
        let totalHorasExtrasNocturnas = 0;
        let totalRecargoNocturno = 0;
        let totalFestivoDiurno = 0;
        let totalFestivoNocturno = 0;

        payrollData.records.forEach(record => {
            const hours = calculateHoursByType(record);
            totalHorasBase += hours.horasBase;
            totalHorasExtrasDiurnas += hours.horasExtrasDiurnas;
            totalHorasExtrasNocturnas += hours.horasExtrasNocturnas;
            totalRecargoNocturno += hours.recargoNocturno;
            totalFestivoDiurno += hours.festivoDiurno;
            totalFestivoNocturno += hours.festivoNocturno;
        });

        // Calcular horas extras diurnas: horasBase - horasALiquidar
        const horasExtrasDiurnas = Math.max(0, totalHorasBase - horasALiquidar);
        // Ajustar horas base (no pueden exceder horasALiquidar)
        const horasBaseFinal = Math.min(totalHorasBase, horasALiquidar);

        return {
            horasBase: horasBaseFinal,
            horasExtrasDiurnas,
            horasExtrasNocturnas: totalHorasExtrasNocturnas,
            recargoNocturno: totalRecargoNocturno,
            festivoDiurno: totalFestivoDiurno,
            festivoNocturno: totalFestivoNocturno
        };
    };

    const toggleHoliday = async (recordId, currentValue) => {
        try {
            await axios.post(`${API_URL}/worked-hours/update`, {
                recordId,
                payload: { esFestivo: !currentValue }
            });
            
            fetchPayrollData();
            
            toast({
                title: 'Actualizado',
                description: `Día marcado como ${!currentValue ? 'festivo' : 'normal'}.`,
            });
        } catch (error) {
            console.error('Error updating holiday:', error);
            toast({
                title: 'Error',
                description: 'No se pudo actualizar el registro.',
                variant: 'destructive',
            });
        }
    };

    // Obtener totales calculados
    const totals = calculateTotals();

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/5 via-background to-primary/10 p-6 md:p-8">
                <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
                <div className="absolute -left-8 -bottom-8 h-32 w-32 rounded-full bg-primary/5 blur-2xl pointer-events-none" />
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                            <Wallet className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                                Liquidación de Personal
                            </h1>
                            <p className="mt-1 text-sm md:text-base text-muted-foreground">
                                Consulta, calcula y guarda la liquidación por empleado con
                                recargos, horas extras y conceptos de nómina.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Configuración de Cálculos - SIEMPRE VISIBLE */}
            <Card>
                <CardHeader>
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                                    <Settings className="h-4 w-4" />
                                </div>
                                Configuración de Cálculos
                            </CardTitle>
                            <CardDescription>
                                Configuración general que aplica para todos los empleados a liquidar.
                            </CardDescription>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowConceptsModal(true)}>
                                <ListChecks className="mr-2 h-4 w-4" />
                                Configurar Conceptos ({concepts.length})
                            </Button>
                            <Dialog open={showMultiplierModal} onOpenChange={setShowMultiplierModal}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" size="sm">
                                        <Settings className="mr-2 h-4 w-4" />
                                        Configurar Recargos
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                        <DialogTitle>Configuración de Recargos y Multiplicadores</DialogTitle>
                                        <DialogDescription>
                                            Ajusta los multiplicadores para cada tipo de hora
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div className="rounded-md border">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Tipo de Hora</TableHead>
                                                        <TableHead>Multiplicador</TableHead>
                                                        <TableHead className="text-right">Acciones</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {[
                                                        { key: 'horaBase', label: 'Hora Base (Ordinaria)', color: 'text-gray-700' },
                                                        { key: 'horaExtraDiurna', label: 'Hora Extra Diurna', color: 'text-orange-600' },
                                                        { key: 'horaExtraNocturna', label: 'Hora Extra Nocturna', color: 'text-purple-600' },
                                                        { key: 'recargoNocturno', label: 'Recargo Nocturno', color: 'text-indigo-600' },
                                                        { key: 'festivoDiurno', label: 'Festivo Diurno', color: 'text-green-600' },
                                                        { key: 'festivoNocturno', label: 'Festivo Nocturno', color: 'text-blue-600' },
                                                    ].map((item) => (
                                                        <TableRow key={item.key}>
                                                            <TableCell className="font-medium">
                                                                <span className={item.color}>{item.label}</span>
                                                            </TableCell>
                                                            <TableCell>
                                                                {editingMultiplier === item.key ? (
                                                                    <Input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        defaultValue={multipliers[item.key]}
                                                                        className="w-24"
                                                                        id={`multiplier-${item.key}`}
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <Badge variant="outline" className="text-base">
                                                                        x{multipliers[item.key]}
                                                                    </Badge>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                {editingMultiplier === item.key ? (
                                                                    <div className="flex gap-2 justify-end">
                                                                        <Button
                                                                            size="sm"
                                                                            disabled={savingMultiplier}
                                                                            onClick={async () => {
                                                                                const input = document.getElementById(`multiplier-${item.key}`);
                                                                                if (!input || !input.value) {
                                                                                    toast({
                                                                                        title: 'Error',
                                                                                        description: 'Ingrese un valor válido.',
                                                                                        variant: 'destructive',
                                                                                    });
                                                                                    return;
                                                                                }
                                                                                
                                                                                const response = await axios.get(`${API_URL}/payroll-config/all`);
                                                                                const config = response.data.data.find(c => c.tipo === item.key);
                                                                                if (config) {
                                                                                    await updateMultiplier(config._id, input.value);
                                                                                } else {
                                                                                    toast({
                                                                                        title: 'Error',
                                                                                        description: 'Configuración no encontrada.',
                                                                                        variant: 'destructive',
                                                                                    });
                                                                                }
                                                                            }}
                                                                        >
                                                                            {savingMultiplier ? 'Guardando...' : 'Guardar'}
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            onClick={() => setEditingMultiplier(null)}
                                                                        >
                                                                            Cancelar
                                                                        </Button>
                                                                    </div>
                                                                ) : (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => setEditingMultiplier(item.key)}
                                                                    >
                                                                        Editar
                                                                    </Button>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <p className="text-sm text-yellow-900">
                                                <strong>Nota:</strong> Los cambios en los multiplicadores afectarán todos los cálculos de nómina.
                                            </p>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                            <Dialog open={showHolidayModal} onOpenChange={setShowHolidayModal}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" size="sm">
                                        <Calendar className="mr-2 h-4 w-4" />
                                        Gestionar Festivos ({holidays.length})
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Gestión de Días Festivos</DialogTitle>
                                    <DialogDescription>
                                        Administra los días festivos de Colombia para cálculos de nómina
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                    {/* Formulario para agregar festivo */}
                                    <div className="grid gap-4 p-4 border rounded-lg bg-muted/50">
                                        <h3 className="font-semibold">Agregar Nuevo Festivo</h3>
                                        <div className="grid gap-4 md:grid-cols-3">
                                            <div>
                                                <label className="text-sm font-medium mb-2 block">Fecha</label>
                                                <Input
                                                    type="date"
                                                    value={newHoliday.fecha}
                                                    onChange={(e) => setNewHoliday({ ...newHoliday, fecha: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium mb-2 block">Nombre</label>
                                                <Input
                                                    type="text"
                                                    placeholder="Ej: Año Nuevo"
                                                    value={newHoliday.nombre}
                                                    onChange={(e) => setNewHoliday({ ...newHoliday, nombre: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium mb-2 block">Tipo</label>
                                                <Select
                                                    value={newHoliday.tipo}
                                                    onValueChange={(value) => setNewHoliday({ ...newHoliday, tipo: value })}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="Nacional">Nacional</SelectItem>
                                                        <SelectItem value="Regional">Regional</SelectItem>
                                                        <SelectItem value="Religioso">Religioso</SelectItem>
                                                        <SelectItem value="Otro">Otro</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <Button onClick={addHoliday} className="w-full">
                                            <Plus className="mr-2 h-4 w-4" />
                                            Agregar Festivo
                                        </Button>
                                    </div>

                                    {/* Lista de festivos */}
                                    <div>
                                        <h3 className="font-semibold mb-3">Festivos Registrados</h3>
                                        <div className="rounded-md border">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Fecha</TableHead>
                                                        <TableHead>Nombre</TableHead>
                                                        <TableHead>Tipo</TableHead>
                                                        <TableHead className="text-right">Acciones</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {holidays.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={4} className="text-center h-24 text-muted-foreground">
                                                                No hay festivos registrados
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        holidays.map((holiday) => (
                                                            <TableRow key={holiday._id}>
                                                                <TableCell className="font-medium">
                                                                    {format(new Date(holiday.fecha), 'dd/MM/yyyy', { locale: es })}
                                                                </TableCell>
                                                                <TableCell>{holiday.nombre}</TableCell>
                                                                <TableCell>
                                                                    <Badge variant="outline">{holiday.tipo}</Badge>
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => deleteHoliday(holiday._id)}
                                                                    >
                                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Horas a Liquidar (mensual)</label>
                            <div className="relative">
                                <Input
                                    type="number"
                                    value={horasALiquidar}
                                    onChange={(e) => setHorasALiquidar(Number(e.target.value))}
                                    placeholder="240"
                                    className="pr-12"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                                    horas
                                </span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Inicio Horario Nocturno</label>
                            <Input
                                type="time"
                                value={nightShiftStart}
                                onChange={(e) => setNightShiftStart(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Fin Horario Nocturno</label>
                            <Input
                                type="time"
                                value={nightShiftEnd}
                                onChange={(e) => setNightShiftEnd(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
                        <span className="flex items-center gap-1.5">
                            <span className="font-semibold text-foreground">Festivos:</span>
                            <span className="text-muted-foreground">{holidays.length} días</span>
                        </span>
                        <span className="hidden md:inline text-muted-foreground/40">|</span>
                        <span className="flex items-center gap-1.5">
                            <span className="font-semibold text-foreground">Horas base:</span>
                            <span className="text-muted-foreground">{horasALiquidar}h</span>
                        </span>
                        <span className="hidden md:inline text-muted-foreground/40">|</span>
                        <span className="flex items-center gap-1.5">
                            <span className="font-semibold text-foreground">Nocturno:</span>
                            <span className="text-muted-foreground">{nightShiftStart} – {nightShiftEnd}</span>
                        </span>
                    </div>
                </CardContent>
            </Card>

            {/* Filtros */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                            <Search className="h-4 w-4" />
                        </div>
                        Seleccionar Empleado y Período
                    </CardTitle>
                    <CardDescription>
                        Elige un empleado y el rango de fechas para consultar su liquidación.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2 space-y-2">
                            <label className="text-sm font-medium text-foreground">
                                Empleado
                                <span className="ml-2 text-xs font-normal text-muted-foreground">
                                    ({employees.length} disponibles)
                                </span>
                            </label>
                            <EmployeeCombobox
                                employees={employees}
                                value={selectedEmployee?._id}
                                onValueChange={handleEmployeeChange}
                                placeholder="Busca un empleado por nombre, cédula o rol…"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Fecha Inicio</label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Fecha Fin</label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t">
                        <Button onClick={fetchPayrollData} disabled={loading || !selectedEmployee}>
                            <Search className="mr-2 h-4 w-4" />
                            {loading ? 'Cargando...' : 'Consultar'}
                        </Button>
                        <Button variant="outline" onClick={handleSetCurrentMonth}>
                            <Calendar className="mr-2 h-4 w-4" />
                            Mes Actual
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Información del empleado */}
            {selectedEmployee && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                                {(selectedEmployee.nombre?.[0] || '?').toUpperCase()}
                            </div>
                            Información del Empleado
                        </CardTitle>
                        <CardDescription>
                            Datos básicos del empleado seleccionado.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <InfoField label="Nombre completo" value={`${selectedEmployee.nombre || ''} ${selectedEmployee.apellido || ''}`.trim() || '—'} />
                            <InfoField label="Documento" value={selectedEmployee.cedula} />
                            <InfoField label="Rol" value={selectedEmployee.rol} />
                            <InfoField label="Centro de Lavado" value={selectedEmployee.lavanderia?.nombre} />
                            <InfoField
                                label="Salario mensual"
                                value={selectedEmployee.salario ? formatCurrency(selectedEmployee.salario) : '—'}
                            />
                        </div>
                    </CardContent>
                </Card>
            )}


            {/* Valores por hora */}
            {payrollData && (
                <Card>
                    <CardHeader>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                                        <TrendingUp className="h-4 w-4" />
                                    </div>
                                    Valores por Hora
                                </CardTitle>
                                <CardDescription>
                                    Calculados en base al salario mensual de{' '}
                                    <span className="font-semibold text-foreground">
                                        {formatCurrency(payrollData.salario || 0)}
                                    </span>
                                </CardDescription>
                            </div>
                            <Badge variant="outline" className="w-fit">
                                {horasALiquidar}h / mes
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                            <HourValueCard
                                label="Hora Ordinaria"
                                multiplier={multipliers.horaBase}
                                value={payrollData.valorHoraBase || 0}
                                color="gray"
                            />
                            <HourValueCard
                                label="Extra Diurna"
                                multiplier={multipliers.horaExtraDiurna}
                                value={payrollData.valorHoraBase * multipliers.horaExtraDiurna || 0}
                                color="orange"
                            />
                            <HourValueCard
                                label="Extra Nocturna"
                                multiplier={multipliers.horaExtraNocturna}
                                value={payrollData.valorHoraBase * multipliers.horaExtraNocturna || 0}
                                color="purple"
                            />
                            <HourValueCard
                                label="Recargo Nocturno"
                                multiplier={multipliers.recargoNocturno}
                                value={payrollData.valorHoraBase * multipliers.recargoNocturno || 0}
                                color="indigo"
                            />
                            <HourValueCard
                                label="Festivo Diurno"
                                multiplier={multipliers.festivoDiurno}
                                value={payrollData.valorHoraBase * multipliers.festivoDiurno || 0}
                                color="green"
                            />
                            <HourValueCard
                                label="Festivo Nocturno"
                                multiplier={multipliers.festivoNocturno}
                                value={payrollData.valorHoraBase * multipliers.festivoNocturno || 0}
                                color="blue"
                            />
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Resumen de horas por tipo */}
            {payrollData && totals && (
                <Card>
                    <CardHeader>
                        <CardTitle>Resumen de Horas y Liquidación</CardTitle>
                        <CardDescription>
                            Totalización de horas por tipo y valor calculado según multiplicadores.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <SummaryCard
                                title="Horas Base"
                                icon={Sun}
                                color="amber"
                                hours={totals.horasBase}
                                value={totals.horasBase * payrollData.valorHoraBase}
                            />
                            <SummaryCard
                                title="Horas Extras Diurnas"
                                icon={Sun}
                                color="orange"
                                hours={totals.horasExtrasDiurnas}
                                value={totals.horasExtrasDiurnas * payrollData.valorHoraBase * multipliers.horaExtraDiurna}
                            />
                            <SummaryCard
                                title="Horas Extras Nocturnas"
                                icon={Moon}
                                color="purple"
                                hours={totals.horasExtrasNocturnas}
                                value={totals.horasExtrasNocturnas * payrollData.valorHoraBase * multipliers.horaExtraNocturna}
                            />
                            <SummaryCard
                                title="Recargo Nocturno"
                                icon={Moon}
                                color="indigo"
                                hours={totals.recargoNocturno}
                                value={totals.recargoNocturno * payrollData.valorHoraBase * multipliers.recargoNocturno}
                            />
                            <SummaryCard
                                title="Festivo Diurno"
                                icon={FileText}
                                color="green"
                                hours={totals.festivoDiurno}
                                value={totals.festivoDiurno * payrollData.valorHoraBase * multipliers.festivoDiurno}
                            />
                            <SummaryCard
                                title="Festivo Nocturno"
                                icon={Moon}
                                color="blue"
                                hours={totals.festivoNocturno}
                                value={totals.festivoNocturno * payrollData.valorHoraBase * multipliers.festivoNocturno}
                            />
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Resumen de totales */}
            {payrollData && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Card>
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                                        Total Horas Trabajadas
                                    </p>
                                    <p className="mt-2 text-3xl font-bold tracking-tight text-foreground">
                                        {payrollData.summary.totalHorasTrabajadas}h
                                    </p>
                                </div>
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                                    <FileText className="h-5 w-5 text-muted-foreground" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                                        Total Horas Autorizadas
                                    </p>
                                    <p className="mt-2 text-3xl font-bold tracking-tight text-emerald-600">
                                        {payrollData.summary.totalHorasAutorizadas}h
                                    </p>
                                </div>
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-950/40">
                                    <FileText className="h-5 w-5 text-emerald-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                                        Total Días Trabajados
                                    </p>
                                    <p className="mt-2 text-3xl font-bold tracking-tight text-foreground">
                                        {payrollData.summary.totalDias}
                                    </p>
                                </div>
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                                    <Calendar className="h-5 w-5 text-muted-foreground" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-primary/30 bg-primary/5">
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                                        Total a Pagar
                                    </p>
                                    <p className="mt-2 text-2xl lg:text-3xl font-bold tracking-tight text-primary truncate">
                                        {totals ? formatCurrency(
                                            (totals.horasBase * payrollData.valorHoraBase) +
                                            (totals.horasExtrasDiurnas * payrollData.valorHoraBase * multipliers.horaExtraDiurna) +
                                            (totals.horasExtrasNocturnas * payrollData.valorHoraBase * multipliers.horaExtraNocturna) +
                                            (totals.recargoNocturno * payrollData.valorHoraBase * multipliers.recargoNocturno) +
                                            (totals.festivoDiurno * payrollData.valorHoraBase * multipliers.festivoDiurno) +
                                            (totals.festivoNocturno * payrollData.valorHoraBase * multipliers.festivoNocturno)
                                        ) : '$0'}
                                    </p>
                                </div>
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 ml-2 shrink-0">
                                    <Wallet className="h-5 w-5 text-primary" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Acción: Generar Liquidación */}
            {payrollData && totals && !showLiquidacion && (
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 rounded-lg border bg-gradient-to-r from-primary/5 to-primary/10 p-4">
                    <div>
                        <p className="font-semibold text-foreground">Listo para liquidar</p>
                        <p className="text-sm text-muted-foreground">
                            Revisa los totales y genera la liquidación para guardar el período.
                        </p>
                    </div>
                    <Button onClick={() => setShowLiquidacion(true)} disabled={!selectedEmployee} size="lg">
                        <FilePlus2 className="mr-2 h-4 w-4" />
                        Generar Liquidación
                    </Button>
                </div>
            )}

            {/* Editor de Liquidación */}
            {payrollData && totals && showLiquidacion && selectedEmployee && (
                <LiquidacionEditor
                    employee={selectedEmployee}
                    salario={payrollData.salario}
                    valorHoraBase={payrollData.valorHoraBase}
                    multipliers={multipliers}
                    totals={totals}
                    payrollData={payrollData}
                    startDate={startDate}
                    endDate={endDate}
                    horasALiquidar={horasALiquidar}
                    nightShiftStart={nightShiftStart}
                    nightShiftEnd={nightShiftEnd}
                    concepts={concepts}
                    user={user}
                    saving={savingSettlement}
                    onSave={handleSaveSettlement}
                    onClose={() => setShowLiquidacion(false)}
                />
            )}

            {/* Tabla de registros */}
            {payrollData && (
                <Card>
                    <CardHeader>
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                            <div>
                                <CardTitle>Detalle de Horas Trabajadas</CardTitle>
                                <CardDescription>
                                    Registros del{' '}
                                    <span className="font-medium text-foreground">
                                        {format(new Date(startDate + 'T00:00:00'), 'dd/MM/yyyy', { locale: es })}
                                    </span>{' '}
                                    al{' '}
                                    <span className="font-medium text-foreground">
                                        {format(new Date(endDate + 'T00:00:00'), 'dd/MM/yyyy', { locale: es })}
                                    </span>
                                </CardDescription>
                            </div>
                            <Button variant="outline" size="sm">
                                <Download className="mr-2 h-4 w-4" />
                                Exportar
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-lg border overflow-hidden">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead className="font-semibold">Fecha</TableHead>
                                            <TableHead className="font-semibold">Ingreso</TableHead>
                                            <TableHead className="font-semibold">Salida</TableHead>
                                            <TableHead className="font-semibold">H. Aut.</TableHead>
                                            <TableHead className="font-semibold">H. Base</TableHead>
                                            <TableHead className="font-semibold">Rec. Noct.</TableHead>
                                            <TableHead className="font-semibold">Fest. Diurno</TableHead>
                                            <TableHead className="font-semibold">Fest. Noct.</TableHead>
                                            <TableHead className="font-semibold text-right">Total Día</TableHead>
                                            <TableHead className="font-semibold">Estado</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                <TableBody>
                                    {payrollData.records.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                                                <div className="flex flex-col items-center gap-2">
                                                    <FileText className="h-8 w-8 opacity-30" />
                                                    <p className="font-medium text-foreground">No hay turnos en este período</p>
                                                    <p className="text-xs">
                                                        No se encontraron registros del {format(new Date(startDate + 'T00:00:00'), 'dd/MM/yyyy', { locale: es })} al {format(new Date(endDate + 'T00:00:00'), 'dd/MM/yyyy', { locale: es })}.
                                                    </p>
                                                    {payrollData._diagnostic && (
                                                        <p className="text-[10px] font-mono mt-2 px-2 py-1 rounded bg-muted">
                                                            id enviado: {payrollData._diagnostic.employeeIdReceived}
                                                            {payrollData._diagnostic.employeeCedula ? ` · cédula: ${payrollData._diagnostic.employeeCedula}` : ''}
                                                            {' · match: '}{payrollData._diagnostic.matchedBy}
                                                        </p>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        payrollData.records.map((record, index) => {
                                            const hours = calculateHoursByType(record);
                                            const totalDia = 
                                                (hours.horasBase * payrollData.valorHoraBase) +
                                                (hours.recargoNocturno * payrollData.valorHoraBase * multipliers.recargoNocturno) +
                                                (hours.festivoDiurno * payrollData.valorHoraBase * multipliers.festivoDiurno) +
                                                (hours.festivoNocturno * payrollData.valorHoraBase * multipliers.festivoNocturno);

                                            return (
                                                <TableRow key={record._id || index}>
                                                    <TableCell className="font-medium">
                                                        {format(new Date(record.fecha), 'dd/MM/yyyy', { locale: es })}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium text-blue-600">
                                                            {record.horaIngresoAutorizada || '—'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium text-blue-600">
                                                            {record.horaSalidaAutorizada || '—'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium text-green-600">
                                                            {record.totalHorasAutorizadas ? `${record.totalHorasAutorizadas}h` : '—'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium text-amber-600">
                                                            {hours.horasBase > 0 ? `${hours.horasBase.toFixed(2)}h` : '—'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium text-indigo-600">
                                                            {hours.recargoNocturno > 0 ? `${hours.recargoNocturno.toFixed(2)}h` : '—'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium text-green-600">
                                                            {hours.festivoDiurno > 0 ? `${hours.festivoDiurno.toFixed(2)}h` : '—'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-medium text-blue-600">
                                                            {hours.festivoNocturno > 0 ? `${hours.festivoNocturno.toFixed(2)}h` : '—'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-bold text-blue-600">
                                                            {formatCurrency(totalDia)}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        {record.isAprobado ? (
                                                            <Badge className="bg-green-500">Aprobado</Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="border-yellow-500 text-yellow-600">Pendiente</Badge>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                                </Table>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Liquidaciones guardadas */}
            <SavedSettlements
                employeeId={selectedEmployee?._id}
                refreshKey={settlementsRefreshKey}
            />

            {/* Modal de configuración de conceptos */}
            <ConfigureConceptsModal
                open={showConceptsModal}
                onOpenChange={setShowConceptsModal}
                concepts={concepts}
                onChanged={fetchConcepts}
            />
        </div>
    );
};

export default EmployeePayrollPage;
